import type {
  ResolvedThemeModeConfiguration,
  TenantThemeConfiguration,
  ThemeAssetConfiguration,
  ThemeMode,
  ThemeModeConfiguration,
  ThemeScope,
  ThemeTokenKey,
  ThemeTokens,
} from '@/types/theme_configuration';
import {
  resolve_theme_shape_defaults,
  resolve_theme_typography_defaults,
} from '@/uptk/core/theme/theme_stitch_import';

const default_light_tokens: Record<ThemeTokenKey, string> = {
  primary: '#2563eb',
  secondary: '#64748b',
  tertiary: '#0ea5e9',
  neutral: '#94a3b8',
  primaryForeground: '#ffffff',
  background: '#f8fafc',
  surface: '#ffffff',
  surfaceMuted: '#e2e8f0',
  foreground: '#0f172a',
  foregroundMuted: '#475569',
  border: '#cbd5e1',
  accent: '#0ea5e9',
  success: '#16a34a',
  warning: '#d97706',
  danger: '#dc2626',
  // UPT-1968 : reprend l'info-600 du design system, pour que les deux
  // systemes de jetons donnent enfin la meme couleur d'information.
  info: '#0284c7',
  color1: '#2563eb',
  color2: '#0f172a',
  color3: '#0ea5e9',
  color4: '#f8fafc',
  color5: '#ffffff',
  color6: '#e2e8f0',
  color7: '#475569',
  color8: '#cbd5e1',
  color9: '#16a34a',
  color10: '#dc2626',
  studioBackground: '#EFF7FB',
};

const default_dark_tokens: Record<ThemeTokenKey, string> = {
  primary: '#3b82f6',
  secondary: '#94a3b8',
  tertiary: '#38bdf8',
  neutral: '#64748b',
  primaryForeground: '#ffffff',
  background: '#020617',
  surface: '#1e293b',
  surfaceMuted: '#111c31',
  foreground: '#f8fafc',
  foregroundMuted: '#cbd5e1',
  border: '#22324d',
  accent: '#38bdf8',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#f87171',
  info: '#38bdf8',
  color1: '#3b82f6',
  color2: '#f8fafc',
  color3: '#38bdf8',
  color4: '#020617',
  color5: '#1e293b',
  color6: '#111c31',
  color7: '#cbd5e1',
  color8: '#22324d',
  color9: '#22c55e',
  color10: '#f87171',
  studioBackground: '#070C12',
};

const empty_assets: ThemeAssetConfiguration = {
  logoLightUrl: null,
  logoDarkUrl: null,
  faviconUrl: null,
};

const default_typography = resolve_theme_typography_defaults();
const default_shape = resolve_theme_shape_defaults();

const default_tokens_by_mode: Record<ThemeMode, Record<ThemeTokenKey, string>> = {
  light: default_light_tokens,
  dark: default_dark_tokens,
};

const create_default_mode_configuration = (mode: ThemeMode): ResolvedThemeModeConfiguration => ({
  tokens: { ...default_tokens_by_mode[mode] },
  assets: { ...empty_assets },
  typography: { ...default_typography },
  shape: { ...default_shape },
});

export const default_tenant_theme_configuration: TenantThemeConfiguration = {
  scopes: {
    portal: {
      modes: {
        light: create_default_mode_configuration('light'),
        dark: create_default_mode_configuration('dark'),
      },
    },
    public: {
      modes: {
        light: create_default_mode_configuration('light'),
        dark: create_default_mode_configuration('dark'),
      },
    },
  },
};

const normalize_tokens = (tokens?: ThemeTokens | null) => {
  const normalized: Partial<Record<ThemeTokenKey, string>> = {};
  if (!tokens) {
    return normalized;
  }

  Object.entries(tokens).forEach(([key, value]) => {
    if (typeof value === 'string' && value.trim().length > 0) {
      normalized[key as ThemeTokenKey] = value.trim();
    }
  });

  return normalized;
};

const normalize_assets = (assets?: ThemeAssetConfiguration | null): ThemeAssetConfiguration => ({
  logoLightUrl: typeof assets?.logoLightUrl === 'string' ? assets.logoLightUrl.trim() || null : null,
  logoDarkUrl: typeof assets?.logoDarkUrl === 'string' ? assets.logoDarkUrl.trim() || null : null,
  faviconUrl: typeof assets?.faviconUrl === 'string' ? assets.faviconUrl.trim() || null : null,
});

const normalize_typography = (typography?: ThemeModeConfiguration['typography'] | null) =>
  resolve_theme_typography_defaults(typography);

// Le tri des valeurs lisibles appartient à `resolve_theme_shape_defaults` :
// filtrer ici en plus jetait les rayons persistés en chaîne (UPT-1968).
const normalize_shape = (shape?: ThemeModeConfiguration['shape'] | null) => {
  return resolve_theme_shape_defaults(shape ?? null);
};

const resolve_mode_configuration = (
  configuration: TenantThemeConfiguration | null | undefined,
  scope: ThemeScope,
  mode: ThemeMode
): ThemeModeConfiguration | null => {
  return configuration?.scopes?.[scope]?.modes?.[mode] ?? null;
};

export const resolve_theme_mode_configuration = (
  configuration: TenantThemeConfiguration | null | undefined,
  scope: ThemeScope,
  mode: ThemeMode
): ResolvedThemeModeConfiguration => {
  const defaults = create_default_mode_configuration(mode);
  const scopedConfiguration = resolve_mode_configuration(configuration, scope, mode);

  return {
    tokens: {
      ...defaults.tokens,
      ...normalize_tokens(scopedConfiguration?.tokens),
    },
    assets: {
      ...defaults.assets,
      ...normalize_assets(scopedConfiguration?.assets),
    },
    typography: {
      ...defaults.typography,
      ...normalize_typography(scopedConfiguration?.typography),
    },
    shape: {
      ...defaults.shape,
      ...normalize_shape(scopedConfiguration?.shape),
    },
  };
};

export const create_toolkit_palette_overrides = (resolved: ResolvedThemeModeConfiguration) => ({
  background: {
    default: resolved.tokens.background,
    paper: resolved.tokens.surface,
    contentFrame: resolved.tokens.background,
  },
  text: {
    primary: resolved.tokens.foreground,
    contrast: resolved.tokens.primaryForeground,
  },
  primary: {
    main: resolved.tokens.primary,
  },
  primaryLight: resolved.tokens.surfaceMuted,
  primaryDark: resolved.tokens.accent,
  color1: resolved.tokens.color1,
  color2: resolved.tokens.color2,
  color3: resolved.tokens.color3,
  color4: resolved.tokens.color4,
  color5: resolved.tokens.color5,
  color6: resolved.tokens.color6,
  color7: resolved.tokens.color7,
  color8: resolved.tokens.color8,
  color9: resolved.tokens.color9,
  color10: resolved.tokens.color10,
  textColor: resolved.tokens.color2,
  borderColor: resolved.tokens.border,
  semanticTokens: {
    ...resolved.tokens,
  },
});

export const copy_theme_configuration = (
  configuration?: TenantThemeConfiguration | null
): TenantThemeConfiguration => {
  const base = configuration ?? default_tenant_theme_configuration;
  return JSON.parse(JSON.stringify(base)) as TenantThemeConfiguration;
};

export const create_persistable_theme_configuration = (
  configuration?: TenantThemeConfiguration | null
): TenantThemeConfiguration => {
  const source = copy_theme_configuration(configuration);
  const to_mode_configuration = (scope: ThemeScope, mode: ThemeMode): ThemeModeConfiguration => {
    const resolved = resolve_theme_mode_configuration(source, scope, mode);
    return {
      tokens: { ...resolved.tokens } as ThemeTokens,
      assets: { ...resolved.assets } as ThemeAssetConfiguration,
      typography: { ...resolved.typography },
      shape: { ...resolved.shape },
    };
  };

  return {
    scopes: {
      portal: {
        modes: {
          light: to_mode_configuration('portal', 'light'),
          dark: to_mode_configuration('portal', 'dark'),
        },
      },
      public: {
        modes: {
          light: to_mode_configuration('public', 'light'),
          dark: to_mode_configuration('public', 'dark'),
        },
      },
    },
  };
};
